import { type FormEvent, useState } from "react";

import { ApiError } from "../api/client";
import type { User } from "../api/types";
import { Alert, Button, Card, Field, Input, PasswordInput } from "../components/ui";
import {
  useChangePassword,
  useCurrentUser,
  useDeleteAccount,
  useLogoutEverywhere,
  useUpdateAccount,
} from "../hooks/useAuth";

function messageOf(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * How you sign in, as opposed to the profile, which is what goes on your
 * documents. Changing the email here does not change the contact email on
 * your resume.
 */
export default function Account() {
  const { user } = useCurrentUser();
  if (!user) return null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Account settings</h1>
        <p className="mt-1 text-sm text-gray-600">
          How you sign in. The name and email on your documents live on your profile.
        </p>
      </header>

      <DetailsCard user={user} />
      <PasswordCard />
      <SessionsCard />
      <DeleteCard />
    </div>
  );
}

function DetailsCard({ user }: { user: User }) {
  const update = useUpdateAccount();
  const [name, setName] = useState(user.display_name);
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState("");
  const [saved, setSaved] = useState(false);

  const emailChanged = email.trim().toLowerCase() !== user.email;
  const dirty = name.trim() !== user.display_name || emailChanged;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    update.mutate(
      {
        display_name: name.trim(),
        ...(emailChanged ? { email: email.trim(), current_password: password } : {}),
      },
      {
        onSuccess: (next) => {
          setName(next.display_name);
          setEmail(next.email);
          setPassword("");
          setSaved(true);
        },
      },
    );
  }

  const error = messageOf(update.error, "Your details could not be saved.");

  return (
    <Card title="Your details">
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        {saved && !dirty && <Alert kind="success">Saved.</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Display name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              required
            />
          </Field>
          <Field label="Sign-in email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>
        </div>

        {emailChanged && (
          <Field label="Current password" hint="Needed to change the email you sign in with.">
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>
        )}

        <Button type="submit" disabled={!dirty || update.isPending}>
          {update.isPending ? "Saving…" : "Save details"}
        </Button>
      </form>
    </Card>
  );
}

function PasswordCard() {
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (next !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    change.mutate(
      { current, next },
      {
        onSuccess: () => {
          setCurrent("");
          setNext("");
          setConfirm("");
        },
      },
    );
  }

  const error = mismatch
    ? "The new passwords don't match."
    : messageOf(change.error, "Your password could not be changed.");

  return (
    <Card title="Password">
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        {change.isSuccess && (
          <Alert kind="success">
            Password changed. Any other devices you were signed in on have been signed out.
          </Alert>
        )}

        <Field label="Current password">
          <PasswordInput
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="New password">
            <PasswordInput
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
          </Field>
          <Field label="Confirm new password">
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
          </Field>
          {/* Below both fields rather than as a Field hint, which would push
              one input lower than its neighbour. */}
          <p className="-mt-2 text-xs text-gray-500 sm:col-span-2">At least 12 characters.</p>
        </div>

        <Button type="submit" disabled={change.isPending}>
          {change.isPending ? "Changing…" : "Change password"}
        </Button>
      </form>
    </Card>
  );
}

function SessionsCard() {
  const logoutEverywhere = useLogoutEverywhere();
  const error = messageOf(logoutEverywhere.error, "That didn't work. Try again.");

  return (
    <Card title="Signed-in devices">
      <div className="space-y-3">
        {error && <Alert kind="error">{error}</Alert>}
        <p className="text-sm text-gray-600">
          Sign out of JobSearcher on every device, including this one.
        </p>
        <Button
          variant="secondary"
          onClick={() => logoutEverywhere.mutate()}
          disabled={logoutEverywhere.isPending}
        >
          Sign out everywhere
        </Button>
      </div>
    </Card>
  );
}

function DeleteCard() {
  const remove = useDeleteAccount();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    remove.mutate(password);
  }

  const error = messageOf(remove.error, "Your account could not be deleted.");

  return (
    <Card title="Delete account">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Permanently deletes your account, profile, applications and every generated document.
          This can't be undone.
        </p>

        {!open ? (
          <Button variant="secondary" onClick={() => setOpen(true)}>
            Delete my account…
          </Button>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            {error && <Alert kind="error">{error}</Alert>}
            <Field label="Enter your password to confirm">
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>
            <div className="flex flex-wrap gap-3">
              <Button type="submit" variant="danger" disabled={remove.isPending}>
                {remove.isPending ? "Deleting…" : "Permanently delete"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setOpen(false);
                  setPassword("");
                  remove.reset();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}
